import React from "react";

const BREAKPOINTS = ["xs", "sm", "md", "lg", "xl", "xxl"];
export type ThemeName = "light" | "dark" | "system" | undefined;

export const bootstrapBreakpoints = {
    xs: 0,
    sm: 1,
    md: 2,
    lg: 3,
    xl: 4,
    xxl: 5,
} as const;

export type LogoType = "square" | "grey" | "standard";
export const LogoBase = "/static/media/branding/";
export const DefaultLogoPaths = {
    STANDARD_LIGHT: "/static/images/logos/7fivefive/logo_light.png",
    STANDARD_DARK: "/static/images/logos/7fivefive/logo_dark.png",
    STANDARD_GREY_LIGHT: "/static/images/logos/7fivefive/logo_light_grey.png",
    STANDARD_GREY_DARK: "/static/images/logos/7fivefive/logo_dark_grey.png",
    SQUARE_LIGHT: "/static/images/logos/7fivefive/logo_square_light.png",
    SQUARE_DARK: "/static/images/logos/7fivefive/logo_square_dark.png",
} as const;
export const CustomerLogoPaths = {
    STANDARD_LIGHT: `${LogoBase}logo-light.png`,
    STANDARD_DARK: `${LogoBase}logo-dark.png`,
} as const;

export const useDefaultLogos = new Set();

export function themeRequiresDarkMode(selectedTheme: ThemeName): boolean {
    if (!selectedTheme) return false;
    return ["dark"].includes(selectedTheme as string);
}

export function getSystemTheme() {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
}

export function getCurrentTheme(): ThemeName {
    const themeName = localStorage.getItem("dashTheme") || getSystemTheme();
    return themeName as ThemeName;
}

export function themeIsDark(): boolean {
    return themeRequiresDarkMode(getCurrentTheme());
}

function getSquareLogoSrc(selectedTheme: ThemeName) {
    return themeRequiresDarkMode(selectedTheme)
        ? DefaultLogoPaths.SQUARE_DARK
        : DefaultLogoPaths.SQUARE_LIGHT;
}

function getGreyLogoSrc(selectedTheme: ThemeName) {
    return themeRequiresDarkMode(selectedTheme)
        ? DefaultLogoPaths.STANDARD_GREY_DARK
        : DefaultLogoPaths.STANDARD_GREY_LIGHT;
}

export function getDefaultLogoSrc(
    selectedTheme: ThemeName,
    logoType: LogoType = "standard",
): string {
    switch (logoType) {
        case "square":
            return getSquareLogoSrc(selectedTheme);
        case "grey":
            return getGreyLogoSrc(selectedTheme);
        default:
            return themeRequiresDarkMode(selectedTheme)
                ? DefaultLogoPaths.STANDARD_DARK
                : DefaultLogoPaths.STANDARD_LIGHT;
    }
}

export function getLogoSrc(
    selectedTheme: ThemeName,
    logoType: LogoType = "standard",
): string {
    if (useDefaultLogos.has(selectedTheme))
        return getDefaultLogoSrc(selectedTheme, logoType);

    return themeRequiresDarkMode(selectedTheme)
        ? CustomerLogoPaths.STANDARD_DARK
        : CustomerLogoPaths.STANDARD_LIGHT;
}

export function resolveCurrentThemeName(): ThemeName {
    const themeName = getCurrentTheme();
    if (themeName === "system") return getSystemTheme();
    return themeName as ThemeName;
}

export function handleLogoError(
    e: React.SyntheticEvent<HTMLImageElement, Event>,
) {
    const currentThemeName: ThemeName = resolveCurrentThemeName();
    if (!useDefaultLogos.has(currentThemeName)) {
        useDefaultLogos.add(currentThemeName);
        (e.target as HTMLImageElement).src =
            getDefaultLogoSrc(currentThemeName);
    }
}

export function setCustomerBackground(): void {
    return;
    // TODO Replace this with actual customer background paths, if set
    const bgUrl = themeIsDark()
        ? 'url("/static/images/customer-bg-dark.jpg")'
        : 'url("/static/images/customer-bg.jpg")';
    setCssVariable("background-img", bgUrl);
}

export function setTheme(theme: ThemeName): void {
    localStorage.setItem("dashTheme", theme as string);
}

export function setCssVariable(cssVariableName: string, value: string) {
    document.documentElement.style.setProperty(`--${cssVariableName}`, value);
}

export function getCssValue(cssVariableName: string, prefix = "bs-") {
    return getComputedStyle(document.documentElement).getPropertyValue(
        `--${prefix}${cssVariableName}`,
    );
}

export function getCurrentBreakpoint(): string {
    return getCssValue("current-breakpoint", "");
}

export function getCurrentBreakpointAsNumber(): number {
    const currentBreakpoint = getCurrentBreakpoint();
    return BREAKPOINTS.indexOf(currentBreakpoint);
}

export function isBiggerThanBreakpoint(breakpoint: string): boolean {
    return getCurrentBreakpointAsNumber() >= BREAKPOINTS.indexOf(breakpoint);
}

export function isSmallerThanBreakpoint(breakpoint: string): boolean {
    return getCurrentBreakpointAsNumber() < BREAKPOINTS.indexOf(breakpoint);
}

export function GraphColourPicker(colourIndex: number) {
    const colours = [
        getCssValue("colour-1", "graph-"),
        getCssValue("colour-2", "graph-"),
        getCssValue("colour-3", "graph-"),
        getCssValue("colour-4", "graph-"),
        getCssValue("colour-5", "graph-"),
        getCssValue("colour-6", "graph-"),
        getCssValue("colour-7", "graph-"),
        getCssValue("colour-8", "graph-"),
        getCssValue("colour-9", "graph-"),
    ];

    if (colourIndex === colours.length - 1) {
        return colours[colours.length - 1];
    } else {
        return colours[colourIndex % colours.length];
    }
}
